import React, { useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  CheckCircle, Rocket, Target, BarChart3, Users, Calendar, 
  FileText, Mail, Clock, BookOpen, Briefcase, TrendingUp,
  MessageSquare, Zap, Globe, PieChart, Settings, ArrowRight,
  Sparkles, GraduationCap, Video, Download
} from 'lucide-react';
import confetti from 'canvas-confetti';

const LearnThankYou = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { email, name, tempPassword } = location.state || {};

  useEffect(() => {
    // Redirect if no state (direct access)
    if (!email) {
      navigate('/learn');
      return;
    }

    // Celebration confetti
    const duration = 3 * 1000;
    const end = Date.now() + duration;

    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: ['#f97316', '#ea580c', '#22c55e']
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: ['#f97316', '#ea580c', '#22c55e']
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    frame();
  }, [email, navigate]);

  const platformFeatures = [
    { icon: Target, title: 'GTM Strategy Builder', desc: 'Create comprehensive go-to-market plans with AI assistance', color: 'orange' },
    { icon: BarChart3, title: 'Market Research Suite', desc: 'TAM/SAM/SOM analysis, competitor intelligence, trend reports', color: 'blue' },
    { icon: Users, title: 'CRM & Contact Management', desc: 'Manage leads, contacts, and sales pipelines', color: 'purple' },
    { icon: Mail, title: 'Email Marketing', desc: 'Campaigns, drip sequences, and automation', color: 'green' },
    { icon: MessageSquare, title: 'Support Ticketing', desc: 'Customer support system with ticket management', color: 'pink' },
    { icon: Globe, title: 'Social Media Automation', desc: 'Schedule posts across Twitter, LinkedIn, Instagram', color: 'cyan' },
    { icon: PieChart, title: 'Sales Projections', desc: '5-year financial forecasting and projections', color: 'yellow' },
    { icon: Settings, title: 'Webforms & Funnels', desc: 'Build landing pages and capture leads', color: 'indigo' }
  ];

  const bootcampModules = [
    { week: 1, title: 'Planning Phase', days: 'Days 1-7', topics: ['Audience Identification', 'Competitive Analysis', 'Market Research', 'Brand Positioning', 'Pitch Development'] },
    { week: 2, title: 'Development Phase', days: 'Days 8-14', topics: ['Online Presence', 'Support Infrastructure', 'Documentation', 'Content Production', 'PR Strategy'] },
    { week: 3, title: 'Deployment Phase', days: 'Days 15-21', topics: ['Launch Strategy', 'Marketing Ops', 'Lead Generation', 'Launch Execution', 'Post-Launch Optimization'] }
  ];

  const bonusModules = [
    'Sales Funnel Mastery',
    'Mid-Funnel Engagement',
    'Pre-Sales Excellence',
    'Customer Success Foundations',
    'Business Operations Toolkit',
    'Compliance & Legal Essentials'
  ];

  const colorMap = {
    orange: 'bg-red-700/20 text-red-700',
    blue: 'bg-blue-500/20 text-blue-500',
    purple: 'bg-purple-500/20 text-purple-500',
    green: 'bg-green-500/20 text-green-500',
    pink: 'bg-pink-500/20 text-pink-500',
    cyan: 'bg-cyan-500/20 text-cyan-500',
    yellow: 'bg-yellow-500/20 text-yellow-500',
    indigo: 'bg-indigo-500/20 text-indigo-500'
  };

  if (!email) return null;

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Welcome Aboard! | SalesQualifyd</title>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white">
        {/* Success Header */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 py-4">
          <div className="max-w-4xl mx-auto px-4 flex items-center justify-center gap-3">
            <CheckCircle className="h-6 w-6" />
            <p className="font-semibold">Payment Successful! Welcome to SalesQualifyd, {name?.split(' ')[0]}!</p>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 py-12">
          
          {/* Welcome Message */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500/20 rounded-full mb-6">
              <Sparkles className="h-10 w-10 text-green-500" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              You&apos;re In! 🎉
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Your journey to GTM mastery begins now. Here&apos;s everything you&apos;ve unlocked:
            </p>
          </div>

          {/* Credentials Card */}
          <div className="bg-gradient-to-r from-red-500/20 to-red-500/20 border border-red-700/30 rounded-2xl p-8 mb-12">
            <div className="flex items-start gap-4 mb-6">
              <div className="bg-red-700 p-3 rounded-xl">
                <Mail className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-1">Check Your Email!</h3>
                <p className="text-gray-400">We&apos;ve sent your login credentials to <span className="text-amber-500 font-semibold">{email}</span></p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                <div className="flex items-center gap-2 mb-3">
                  <Rocket className="h-5 w-5 text-red-700" />
                  <h4 className="font-semibold">Platform Access (Instant)</h4>
                </div>
                <p className="text-sm text-gray-400 mb-3">Access all tools at your dashboard</p>
                <Link 
                  to="/login" 
                  className="inline-flex items-center gap-2 bg-red-700 hover:bg-red-800 text-white px-4 py-2 rounded-lg font-medium transition-all"
                >
                  Login to Platform <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              
              <div className="bg-white/5 rounded-xl p-5 border border-white/10">
                <div className="flex items-center gap-2 mb-3">
                  <GraduationCap className="h-5 w-5 text-blue-500" />
                  <h4 className="font-semibold">LMS Course Access</h4>
                </div>
                <p className="text-sm text-gray-400 mb-3">Video courses at learn.salesqualifyd.com</p>
                <div className="flex items-center gap-2 text-yellow-500 text-sm">
                  <Clock className="h-4 w-4" />
                  <span>Activating within 24 hours (separate login)</span>
                </div>
              </div>
            </div>
          </div>

          {/* Platform Features */}
          <div className="mb-16">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold mb-3">🚀 Your Platform Tools</h2>
              <p className="text-gray-400">Lifetime access to the complete SalesQualifyd suite</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {platformFeatures.map((feature, idx) => (
                <div key={idx} className="bg-white/5 border border-white/10 rounded-xl p-5 hover:border-white/20 transition-all">
                  <div className={`inline-flex p-3 rounded-lg mb-4 ${colorMap[feature.color]}`}>
                    <feature.icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-semibold mb-2">{feature.title}</h3>
                  <p className="text-sm text-gray-400">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Bootcamp Curriculum */}
          <div className="mb-16">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold mb-3">📚 21-Day GTM Bootcamp</h2>
              <p className="text-gray-400">Your complete video curriculum (activating soon)</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {bootcampModules.map((module, idx) => (
                <div key={idx} className="bg-white/5 border border-white/10 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-red-700 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold">
                      {module.week}
                    </div>
                    <div>
                      <h3 className="font-semibold">{module.title}</h3>
                      <p className="text-sm text-gray-400">{module.days}</p>
                    </div>
                  </div>
                  <ul className="space-y-2">
                    {module.topics.map((topic, tIdx) => (
                      <li key={tIdx} className="flex items-center gap-2 text-sm text-gray-300">
                        <Video className="h-4 w-4 text-red-700 flex-shrink-0" />
                        {topic}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Bonus Modules */}
          <div className="mb-16">
            <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-2xl p-8">
              <div className="text-center mb-8">
                <span className="inline-block bg-green-500 text-white px-4 py-1 rounded-full text-sm font-semibold mb-3">
                  🎁 BONUS INCLUDED
                </span>
                <h2 className="text-2xl font-bold">6 Additional Modules</h2>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                {bonusModules.map((module, idx) => (
                  <div key={idx} className="flex items-center gap-3 bg-white/5 rounded-lg p-4">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-sm">{module}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Templates & Resources */}
          <div className="mb-16">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold mb-3">📦 Templates & Resources</h2>
              <p className="text-gray-400">Downloadable assets available in the platform</p>
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              {['Audience Analysis Toolkit', 'Market Research Workbook', 'Pitch Deck Templates', 'GTM Strategy Calendar', 'Marketing SOPs', 'Sales Toolkit'].map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2">
                  <Download className="h-4 w-4 text-red-700" />
                  <span className="text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">🎯 Your Next Steps</h2>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="p-4">
                <div className="bg-red-700/20 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-red-700 font-bold text-xl">1</span>
                </div>
                <h3 className="font-semibold mb-2">Check Your Email</h3>
                <p className="text-sm text-gray-400">Get your login credentials</p>
              </div>
              <div className="p-4">
                <div className="bg-red-700/20 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-red-700 font-bold text-xl">2</span>
                </div>
                <h3 className="font-semibold mb-2">Login to Platform</h3>
                <p className="text-sm text-gray-400">Start exploring the tools</p>
              </div>
              <div className="p-4">
                <div className="bg-red-700/20 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-red-700 font-bold text-xl">3</span>
                </div>
                <h3 className="font-semibold mb-2">Wait for LMS Access</h3>
                <p className="text-sm text-gray-400">Video courses within 24 hrs</p>
              </div>
            </div>
            <Link 
              to="/login" 
              className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-red-500 hover:from-red-800 hover:to-red-600 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-lg shadow-red-700/30"
            >
              <Rocket className="h-5 w-5" />
              Access Your Dashboard Now
            </Link>
          </div>

          {/* Support */}
          <div className="text-center mt-12 text-gray-500">
            <p>Need help? Email us at <a href="mailto:support@salesqualifyd.com" className="text-red-700 hover:underline">support@salesqualifyd.com</a></p>
          </div>
        </div>
      </div>
    </>
  );
};

export default LearnThankYou;
